import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'
import { BadgeCheck } from 'lucide-react'
import { formatAmount, type PaymentState } from '@concours/shared'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Empty,
  StatusBadge,
  Table,
  useAuth,
  useCan,
  type Column,
} from '@concours/ui'
import { when } from '../../lib/competition-types'
import type { AdminPaymentRow } from '../../lib/payments'
import type { Paginated } from '../../lib/rbac-types'
import { ConfirmPaymentModal } from '../payments/PaymentPage'

/** Onglet « Paiement » de la fiche : état du dossier et transactions (si `payment.view`). */
export function PaymentHistory({
  applicationNumber,
  status,
}: {
  applicationNumber: string
  status: PaymentState
}) {
  const { session } = useAuth()
  const canView = useCan('payment.view')
  const canConfirm = useCan('payment.manual_entry')
  const [confirming, setConfirming] = useState<AdminPaymentRow | null>(null)
  const payments = useQuery({
    queryKey: ['admin', 'payments', 'of', applicationNumber],
    queryFn: () =>
      session.api
        .get<Paginated<AdminPaymentRow>>('/admin/payments', {
          query: { search: applicationNumber, perPage: 50 },
        })
        .then((r) => r.data.filter((p) => p.applicationNumber === applicationNumber)),
    enabled: canView,
  })
  const columns: Column<AdminPaymentRow>[] = [
    {
      key: 'reference',
      header: 'Référence',
      cell: (p) => (
        <Link to={`/paiements/${p.id}`} className="mono">
          {p.reference}
        </Link>
      ),
    },
    { key: 'method', header: 'Moyen', cell: (p) => p.methodLabel },
    {
      key: 'amount',
      header: 'Montant',
      numeric: true,
      cell: (p) => formatAmount(p.total, p.currency),
    },
    {
      key: 'status',
      header: 'État',
      cell: (p) => <StatusBadge domain="payment" status={p.status} />,
    },
    { key: 'date', header: 'Date', cell: (p) => when(p.paidAt ?? p.createdAt) },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'end',
      cell: (p) =>
        canConfirm &&
        ['processing', 'failed', 'cancelled'].includes(p.status) && (
          <Button
            size="sm"
            variant="secondary"
            icon={<BadgeCheck />}
            onClick={() => setConfirming(p)}
          >
            Valider
          </Button>
        ),
    },
  ]
  return (
    <Card>
      {confirming && (
        <ConfirmPaymentModal detail={confirming} onClose={() => setConfirming(null)} />
      )}
      <CardHeader
        title="Paiement des frais"
        actions={<StatusBadge domain="payment" status={status} />}
      />
      <CardBody>
        {canView ? (
          <Table
            columns={columns}
            rows={payments.data ?? []}
            rowKey={(p) => p.id}
            loading={payments.isPending}
            caption="Transactions du dossier"
            empty={<Empty title="Aucune transaction pour ce dossier" />}
          />
        ) : (
          <p className="text-muted" style={{ margin: 0 }}>
            Le détail des transactions est réservé aux profils financiers.
          </p>
        )}
      </CardBody>
    </Card>
  )
}
